import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import {
  getWebhookLogs,
  getWebhookStats,
  retryWebhook,
  getWebhookHealth,
  WebhookLogsResponse,
  WebhookStats,
  RetryWebhookResponse,
} from '../api/tatumWebhook';

/**
 * Hook to fetch webhook logs
 */
export const useWebhookLogs = (status?: string, limit: number = 100, skip: number = 0) => {
  return useQuery<WebhookLogsResponse, Error>({
    queryKey: ['webhookLogs', status, limit, skip],
    queryFn: () => getWebhookLogs(status, limit, skip),
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000, // Consider data stale after 10 seconds
  });
};

/**
 * Hook to fetch webhook statistics
 */
export const useWebhookStats = () => {
  return useQuery<WebhookStats, Error>({
    queryKey: ['webhookStats'],
    queryFn: getWebhookStats,
    refetchInterval: 30000, // Refetch every 30 seconds
    staleTime: 10000,
  });
};

/**
 * Hook to fetch webhook server health
 */
export const useWebhookHealth = () => {
  return useQuery({
    queryKey: ['webhookHealth'],
    queryFn: getWebhookHealth,
    refetchInterval: 60000, // Refetch every minute
    staleTime: 30000,
  });
};

/**
 * Hook to retry a failed webhook
 */
export const useRetryWebhook = () => {
  const queryClient = useQueryClient();

  return useMutation<RetryWebhookResponse, Error, string>({
    mutationFn: retryWebhook,
    onSuccess: (data) => {
      message.success(`Webhook retried successfully: ${data.message}`);
      // Invalidate and refetch webhook logs
      queryClient.invalidateQueries({ queryKey: ['webhookLogs'] });
      queryClient.invalidateQueries({ queryKey: ['webhookStats'] });
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Failed to retry webhook';
      message.error(`Retry failed: ${errorMessage}`);
    },
  });
};

